import React from "react";
import { usePortal } from "../../contexts/api";
import type {
  TheoryTestTemplate,
  QuestionForm,
  TheoryTest,
} from "../../api/theoryTests/TheoryTest.typed";
import { Box, Typography, Button, Checkbox, CircularProgress, Modal } from "@mui/joy";
import { CloseRoundedIcon } from "../../styles/StyledMUI";
import IconButtonCTA from "../common/IconButtonCTA";
import DarkCard from "../common/DarkCard";
import DarkTypography from "../common/DarkTypography";
import { SClose, SSave } from "../../styles/StyledMUI";
import { useAlert } from "../../contexts/app/notificationToast";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import TheoryTestHistoryModal from "./TheoryTestHistoryModal";
import {
  colorTokens,
  ctaAccentColor,
  ctaAccentHoverColor,
  ctaAccentTextColor,
  portalDockedActionsBarSx,
  roundActionIconButtonSx,
  textTokens,
  withAlpha,
} from "../../styles/globalStyles";

interface TheoryTestPageProps {
  customerId?: number;
  trainingTemplateId?: number;
  firstName?: string;
  agency?: string;
  qualification?: string;
  theoryTests?: TheoryTest[];
  onClose?: () => void;
  onCancel?: () => void;
}

type TheoryHeaderProps = {
  agency?: string;
  qualification?: string;
};

function TheoryHeader({ agency, qualification }: TheoryHeaderProps) {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "100px",
        backgroundColor: colorTokens.bg.app,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 1,
        zIndex: 1000,
        borderBottom: `1px solid ${colorTokens.border.default}`,
        px: 2,
      }}
    >
      <Typography
        level="h3"
        sx={{
          color: textTokens.color.title,
          fontSize: { xs: "18px", sm: "24px" },
          textAlign: "center",
        }}
      >
        QCM
      </Typography>
      <Typography
        level="body-sm"
        sx={{
          color: textTokens.color.secondary,
          fontSize: { xs: "12px", sm: "14px" },
          textAlign: "center",
        }}
      >
        {agency} - {qualification}
      </Typography>
    </Box>
  );
}

type TheoryQuestionCardProps = {
  question: TheoryTestTemplate["TheoryTest"][number];
  checkedAnswers: string[];
  onAnswerChange: (answer: string, checked: boolean) => void;
};

function TheoryQuestionCard({ question, checkedAnswers, onAnswerChange }: TheoryQuestionCardProps) {
  return (
    <DarkCard sx={{ mb: 3 }}>
      <DarkTypography
        level="h4"
        colorVariant="primary"
        sx={{
          marginBottom: "12px",
          fontSize: { xs: "14px", sm: "16px" },
        }}
      >
        {question.QuestionText}
      </DarkTypography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {question.PossibleAnswers.map((option, index) => {
          const isChecked = checkedAnswers.includes(option);
          return (
            <Box
              key={index}
              onClick={() => onAnswerChange(option, !isChecked)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                backgroundColor: isChecked
                  ? withAlpha(ctaAccentColor, 0.12)
                  : colorTokens.bg.surfaceMuted,
                border: `1px solid ${isChecked ? ctaAccentColor : colorTokens.border.default}`,
                borderRadius: "6px",
                p: { xs: 0.75, sm: 1 },
                transition: "background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": {
                  backgroundColor: isChecked
                    ? withAlpha(ctaAccentColor, 0.18)
                    : colorTokens.bg.hover,
                  borderColor: ctaAccentColor,
                },
              }}
            >
              <Checkbox
                checked={isChecked}
                onChange={(e) => onAnswerChange(option, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  mr: 1,
                  "& .MuiSvgIcon-root": {
                    fontSize: { xs: 20, sm: 24 },
                  },
                }}
              />
              <DarkTypography colorVariant="primary" sx={{ fontSize: { xs: "12px", sm: "14px" } }}>
                {option}
              </DarkTypography>
            </Box>
          );
        })}
      </Box>
    </DarkCard>
  );
}

export function TheoryTestPage({
  customerId,
  trainingTemplateId,
  agency,
  qualification,
  theoryTests,
  onClose,
  onCancel,
}: TheoryTestPageProps) {
  const { getPublicTheoryTestTemplate, submitTheoryTest } = usePortal();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();

  const [template, setTemplate] = React.useState<TheoryTestTemplate | null>(null);
  const [answers, setAnswers] = React.useState<Record<number, string[]>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [testResult, setTestResult] = React.useState<TheoryTest | null>(null);
  const [showResultModal, setShowResultModal] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const [updatedTheoryTests, setUpdatedTheoryTests] = React.useState<TheoryTest[]>(
    theoryTests || [],
  );

  React.useEffect(() => {
    const fetchTemplate = async () => {
      if (trainingTemplateId) {
        try {
          const data = await getPublicTheoryTestTemplate(String(trainingTemplateId));
          setTemplate(data);
        } catch (error) {
          showAlert("Erreur lors du chargement du test", "danger");
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchTemplate();
  }, [trainingTemplateId, getPublicTheoryTestTemplate, showAlert]);

  React.useEffect(() => {
    const sortedTests = (theoryTests || []).sort((a, b) => b.Attempt - a.Attempt);
    setUpdatedTheoryTests(sortedTests);
  }, [theoryTests]);

  const handleAnswerChange = (questionId: number, answer: string, checked: boolean) => {
    setAnswers((prev) => {
      const currentAnswers = prev[questionId] || [];
      if (checked) {
        return {
          ...prev,
          [questionId]: [...currentAnswers, answer],
        };
      } else {
        return {
          ...prev,
          [questionId]: currentAnswers.filter((a) => a !== answer),
        };
      }
    });
  };

  const handleSubmit = async (e?: React.BaseSyntheticEvent) => {
    e?.preventDefault();
    if (!trainingTemplateId || !customerId || !template) return;

    const unansweredQuestions = template.TheoryTest.filter(
      (question) => !answers[question.ID] || answers[question.ID].length === 0,
    );

    if (unansweredQuestions.length > 0) {
      showAlert(
        `Il reste ${unansweredQuestions.length} question${unansweredQuestions.length > 1 ? "s" : ""} sans réponse`,
        "warning",
      );
      return;
    }

    const confirmed = await showConfirmation(
      "Confirmation",
      "Êtes-vous sûr de vouloir soumettre le test ? Cette action est définitive.",
    );

    if (!confirmed) return;

    const formData: QuestionForm[] = template.TheoryTest.map((question) => ({
      ID: question.ID,
      QuestionText: question.QuestionText,
      ProvidedAnswers: answers[question.ID] || [],
    }));

    setIsSubmitting(true);
    try {
      const result = await submitTheoryTest(String(trainingTemplateId), String(customerId), {
        CustomerID: customerId,
        TrainingTemplateID: trainingTemplateId,
        Form: formData,
      });

      if (result) {
        setTestResult(result);
        setShowResultModal(true);
        setUpdatedTheoryTests((prevTests) => {
          const newTests = [...prevTests, result];
          return newTests.sort((a, b) => b.Attempt - a.Attempt);
        });
      } else {
        throw new Error("Pas de résultat reçu");
      }
    } catch (error) {
      if (error instanceof Error) {
        showAlert(`Erreur lors de la soumission du test: ${error.message}`, "danger");
      } else {
        showAlert("Erreur lors de la soumission du test", "danger");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseResult = async () => {
    if (onClose) {
      await onClose();
    }
    setShowResultModal(false);
  };

  const handleCancelTest = async () => {
    const confirmed = await showConfirmation(
      "QCM",
      "Êtes-vous sûr de vouloir annuler le QCM ? Vos réponses seront perdues.",
    );
    if (!confirmed) return;
    showAlert("QCM annulé", "warning");
    if (onCancel) {
      onCancel();
    }
  };

  const handleShowHistory = () => {
    setShowResultModal(false);
    setShowHistory(true);
  };

  const handleCloseHistoryAndShowResult = () => {
    setShowHistory(false);
    setShowResultModal(true);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          backgroundColor: colorTokens.bg.app,
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size="lg" />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: colorTokens.bg.app,
        color: textTokens.color.primary,
        fontFamily: textTokens.fontFamily,
        minHeight: "100vh",
        position: "relative",
      }}
    >
      <TheoryHeader agency={agency} qualification={qualification} />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: "absolute",
          top: "100px",
          left: 0,
          right: 0,
          bottom: "100px",
          overflowY: "auto",
          padding: 3,
        }}
      >
        <Typography
          level="body-sm"
          sx={{
            color: textTokens.color.muted,
            mb: 2,
            fontStyle: "italic",
            textAlign: "center",
          }}
        >
          Note : Plusieurs réponses peuvent être correctes pour chaque question
        </Typography>
        {template?.TheoryTest.map((question) => (
          <TheoryQuestionCard
            key={question.ID}
            question={question}
            checkedAnswers={answers[question.ID] || []}
            onAnswerChange={(answer, checked) => handleAnswerChange(question.ID, answer, checked)}
          />
        ))}
      </Box>

      <Box
        sx={{
          ...portalDockedActionsBarSx,
          width: "auto",
          minWidth: { xs: "132px", sm: "144px" },
          gap: 1,
        }}
      >
        <IconButtonCTA
          type="button"
          onClick={() => void handleSubmit()}
          disabled={isSubmitting}
          ctaVariant="success"
          aria-label="Valider et soumettre le test"
          sx={{ ...roundActionIconButtonSx }}
        >
          {isSubmitting ? <CircularProgress size="sm" /> : <SSave iconColor="inherit" />}
        </IconButtonCTA>
        <IconButtonCTA
          type="button"
          onClick={() => void handleCancelTest()}
          disabled={isSubmitting}
          ctaVariant="danger"
          aria-label="Annuler le QCM"
          sx={{ ...roundActionIconButtonSx }}
        >
          <SClose iconColor="inherit" />
        </IconButtonCTA>
      </Box>

      <Modal
        open={showResultModal}
        onClose={handleCloseResult}
        sx={{
          zIndex: 195000,
          "& .MuiModal-backdrop": {
            zIndex: -1,
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
            backgroundColor: withAlpha(colorTokens.bg.overlay, 0.55),
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100%",
            p: { xs: 1.5, sm: 2 },
            boxSizing: "border-box",
          }}
        >
          <DarkCard
            sx={{
              width: "100%",
              maxWidth: { xs: "min(95vw, 500px)", sm: 500 },
              p: 3,
              boxShadow: `0 8px 32px ${withAlpha(colorTokens.bg.overlay, 0.45)}`,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                gap: 1,
                textAlign: "left",
              }}
            >
              <DarkTypography component="h2" level="title-md" colorVariant="title">
                Résultats
              </DarkTypography>
              <IconButtonCTA
                aria-label="Fermer"
                ctaVariant="neutral"
                size="sm"
                onClick={handleCloseResult}
              >
                <CloseRoundedIcon />
              </IconButtonCTA>
            </Box>
            {testResult && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <DarkTypography
                  level="h2"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    fontWeight: 700,
                    color:
                      testResult.Result >= 80
                        ? colorTokens.status.success
                        : colorTokens.status.danger,
                  }}
                >
                  {testResult.Result >= 80 ? "Réussi" : "Échoué"}
                </DarkTypography>
                <DarkTypography
                  level="title-lg"
                  sx={{
                    mb: 2,
                    fontSize: { xs: "1rem", sm: "1.25rem" },
                    fontWeight: 600,
                    color:
                      testResult.Result >= 80
                        ? colorTokens.status.success
                        : colorTokens.status.danger,
                  }}
                >
                  Note :{" "}
                  {(testResult.Result / 100).toLocaleString("fr-FR", {
                    style: "percent",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  })}
                </DarkTypography>
                <DarkTypography
                  level="body-md"
                  colorVariant="primary"
                  sx={{ mb: 1, fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Test effectué le :{" "}
                  {new Date(testResult.LastAttemptDate).toLocaleDateString("fr-FR")}
                </DarkTypography>
                <DarkTypography
                  level="body-md"
                  colorVariant="primary"
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  Tentative n° : {testResult.Attempt}
                </DarkTypography>
              </Box>
            )}
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap", mt: 2 }}
            >
              {updatedTheoryTests && updatedTheoryTests.length > 1 && (
                <Button
                  variant="soft"
                  onClick={handleShowHistory}
                  size="lg"
                  sx={{
                    flex: 1,
                    minWidth: { xs: "100%", sm: "auto" },
                    backgroundColor: colorTokens.bg.surfaceMuted,
                    color: textTokens.color.primary,
                    border: `1px solid ${colorTokens.border.default}`,
                    "&:hover": {
                      backgroundColor: colorTokens.bg.hover,
                    },
                  }}
                >
                  Voir résultats
                </Button>
              )}
              <Button
                variant="solid"
                onClick={handleCloseResult}
                size="lg"
                sx={{
                  flex: 1,
                  minWidth: { xs: "100%", sm: "auto" },
                  backgroundColor: colorTokens.text.title,
                  color: ctaAccentTextColor,
                  "&:hover": {
                    backgroundColor: ctaAccentHoverColor,
                    color: ctaAccentTextColor,
                  },
                }}
              >
                Terminé
              </Button>
            </Box>
          </DarkCard>
        </Box>
      </Modal>

      {updatedTheoryTests && (
        <TheoryTestHistoryModal
          tests={updatedTheoryTests}
          template={template}
          isOpen={showHistory}
          onClose={handleCloseHistoryAndShowResult}
        />
      )}
    </Box>
  );
}
